/**
 * Personalization Quiz
 *
 * A fast, optional 3-question flow to personalize agent suggestions.
 * Local state only - no backend persistence.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight } from 'lucide-react';

interface PersonalizationQuizProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (answers: QuizAnswers) => void;
  existingAnswers?: QuizAnswers | null;
}

export interface QuizAnswers {
  intentType: string;
  workSource: string;
  firstGoal: string;
}

const QUESTIONS = [
  {
    id: 'intentType',
    question: 'What are you here to do?',
    options: [
      { value: 'build', label: 'Build / code' },
      { value: 'learn', label: 'Learn / explore' },
      { value: 'contribute', label: 'Contribute to projects' },
      { value: 'manage', label: 'Manage work' }
    ]
  },
  {
    id: 'workSource',
    question: 'Where is your work today?',
    options: [
      { value: 'github', label: 'GitHub' },
      { value: 'local', label: 'Local projects' },
      { value: 'starting', label: 'Just getting started' }
    ]
  },
  {
    id: 'firstGoal',
    question: 'What do you want help with first?',
    options: [
      { value: 'create', label: 'Creating something new' },
      { value: 'improve', label: 'Improving existing work' },
      { value: 'understand', label: 'Understanding my work' },
      { value: 'safe', label: 'Making safe changes' }
    ]
  }
];

export const PersonalizationQuiz: React.FC<PersonalizationQuizProps> = ({
  isOpen,
  onClose,
  onComplete,
  existingAnswers
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Partial<QuizAnswers>>(existingAnswers || {});

  // Reset step when opening
  useEffect(() => {
    if (isOpen) {
      if (existingAnswers) {
        setCurrentStep(3); // Skip to completed state
        setAnswers(existingAnswers);
      } else {
        setCurrentStep(0);
        setAnswers({});
      }
    }
  }, [isOpen, existingAnswers]);

  const handleSelect = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    // Auto-advance after short delay
    setTimeout(() => {
      if (currentStep < 2) {
        setCurrentStep(prev => prev + 1);
      } else {
        // Complete
        const finalAnswers = { ...answers, [questionId]: value } as QuizAnswers;
        onComplete(finalAnswers);
        // Save to localStorage
        try {
          localStorage.setItem('xableai_quiz_answers', JSON.stringify(finalAnswers));
          console.log('[Quiz] Completed:', finalAnswers);
        } catch (e) {
          console.warn('[Quiz] Failed to save to localStorage:', e);
        }
      }
    }, 300);
  };

  const handleSkip = () => {
    console.log('[Quiz] Skipped');
    onClose();
  };

  const handleEdit = () => {
    setCurrentStep(0);
    setAnswers({});
  };

  // Already completed state
  if (isOpen && currentStep === 3 && existingAnswers) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-md w-full"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Your Preferences</h2>
                <button onClick={onClose} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3 mb-6">
                {QUESTIONS.map((q, idx) => (
                  <div key={q.id} className="text-sm">
                    <p className="text-gray-400 mb-1">{q.question}</p>
                    <p className="text-white font-medium">
                      {q.options.find(opt => opt.value === existingAnswers[q.id as keyof QuizAnswers])?.label}
                    </p>
                  </div>
                ))}
              </div>
              <button
                onClick={handleEdit}
                className="w-full bg-purple-600 text-white py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Edit Preferences
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Quiz flow
  return (
    <AnimatePresence>
      {isOpen && currentStep < 3 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-purple-400">
                  {currentStep + 1}/3
                </span>
                <div className="flex space-x-1">
                  {[0, 1, 2].map(i => (
                    <div
                      key={i}
                      className={`w-8 h-1 rounded-full ${
                        i <= currentStep ? 'bg-purple-500' : 'bg-gray-700'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <button onClick={handleSkip} className="text-gray-400 hover:text-white text-sm">
                Skip
              </button>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <h2 className="text-xl font-semibold text-white mb-6">
                  {QUESTIONS[currentStep].question}
                </h2>
                <div className="space-y-3">
                  {QUESTIONS[currentStep].options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleSelect(QUESTIONS[currentStep].id, option.value)}
                      className="w-full text-left p-4 bg-gray-900 border border-gray-700 rounded-lg hover:border-purple-500 hover:bg-purple-900/20 transition-all"
                    >
                      <span className="text-white">{option.label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
